import {Box, Button, Tooltip, Typography, useMediaQuery} from '@mui/material';
import WaterMiniChart from '../WaterMiniChart';
import {useEffect, useState} from 'react';
import MMLogo from '../../assets/mobileMonitoring-white.png';
import {fillMissingValues, PUBLIC_DATA_24H, PUBLIC_DATA_7D, readSafe, UNITS} from '../../utils';
import moment from 'moment/moment';
import {translation} from '../translation';

const MOBILE_SCREEN_WIDTH = 767;

export default function UGHorizontalWidgetMinimalResponsive() {
  const [interval, setInterval] = useState('24h');
  const [selected, setSelected] = useState('temp');
  const [chartData, setChartData] = useState({});
  const en = window.location.href.includes('lang=en');
  const color = window.location.href.includes('color=') ? window.location.href.split('color=')[1] : "rgb(18,44,111)";
  const t = translation[en ? 'en' : 'pl'];
  const isMobile = useMediaQuery(`(max-width: ${MOBILE_SCREEN_WIDTH}px)`);

  useEffect(() => {
    fetch(interval === '24h' ? PUBLIC_DATA_24H : PUBLIC_DATA_7D).then(response => {
      response.json().then(result => setChartData(fillMissingValues(result['UGW_WATER_BORUCINO'], ['temp', 'do'])));
    }).catch(error => console.error(error));
  }, [interval]);

  useEffect(() => {
    window.chartistTooltipUnits = UNITS[selected];
    window.chartistTooltipColor = color;
  }, [selected]);

  const currentChartData = chartData[selected] ? chartData[selected].map(l => ({
    meta: moment(l[0]).format('DD.MM.YYYY HH:mm'),
    timestamp: l[0],
    value: l[1]
  })) : [];

  const temp = readSafe(chartData, 'temp');
  const oxygen = readSafe(chartData, 'do');

  const container = {
    height: isMobile ? 350 : 100,
    display: "flex",
    flexDirection: isMobile ? 'column' : 'row',
    backgroundColor: color,
    justifyContent: isMobile ? "flex-start" : "space-between",
    alignItems: "center",
    padding: isMobile ? 0 : 6,
    marginTop: 40,
    boxShadow: '0 6px 24px rgba(0,0,0,0.18)',
  };

  const logoWrapper = {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-around",
    gap: 8,
    paddingLeft: 12,
  };

  const logo = {
    width: "200px"
  };

  const btnWrapper = {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: 8,
    paddingLeft: isMobile ? 0 : 12,
  };

  const intervalBtn = (active) => ({
    cursor: 'pointer',
    width: 50,
    minWidth: 50,
    color: active ? color : 'whitesmoke',
    backgroundColor: active ? 'whitesmoke' : 'transparent',
    borderColor: 'rgba(255,255,255,0.45)',
    transition: 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease',
    "&:hover": {
      backgroundColor: "white",
      color: "#000",
      borderColor: 'white',
    }
  });

  return (
    <Box style={container}>
      <Box sx={{
        display: "flex",
        alignItems: "center",
        flexDirection: isMobile ? "column" : "row",
        gap: isMobile ? 2 : 3,
        paddingTop: isMobile ? 2 : 0,
      }}>
        <Box style={logoWrapper}>
          <Tooltip placement={'right-start'} sx={{cursor: 'pointer'}}
                   title={<Typography variant={'caption'}>{t.mobileMonitoring}</Typography>}>
            <a href={'https://mobilemonitoring.pl'}>
              <img style={logo} src={MMLogo}/>
            </a>
          </Tooltip>
          <Tooltip placement={'right-start'} sx={{cursor: 'pointer'}}
                   title={<Typography variant={'caption'}>{t.exampleData}</Typography>}>
            <Box>
              <Typography ml={4} fontWeight={'bold'} variant={'button'} color={'whitesmoke'}>
                {t.buoyName}
              </Typography>
            </Box>
          </Tooltip>
        </Box>

        <Box style={btnWrapper}>
          <MeasurementRow width={180} label={t.tempLong} value={temp} units={UNITS.temp} en={en}
                          selected={selected === 'temp'}
                          onClick={() => setSelected('temp')}
          />
          <MeasurementRow width={180} label={t.do} value={oxygen} units={UNITS.do} en={en}
                          selected={selected === 'do'}
                          onClick={() => setSelected('do')}
          />
        </Box>

        <Box sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          paddingTop: isMobile ? 2 : 0,
          flexDirection: isMobile ? "row" : "column",
          justifyContent: isMobile ? "space-around" : "center",
        }}>
          <Button variant={'outlined'} sx={intervalBtn(interval === '24h')} onClick={() => setInterval('24h')}>
            24h
          </Button>
          <Button variant={'outlined'} sx={intervalBtn(interval === '7d')} onClick={() => setInterval('7d')}>
            7d
          </Button>
        </Box>
      </Box>

      <Box ml={isMobile ? -5 : 0} sx={{width: isMobile ? "100%" : "80%", display: "flex", flexDirection: "column"}}>
        <WaterMiniChart height={'100px'} data={currentChartData}/>
      </Box>
    </Box>
  )
}

const MeasurementRow = ({width, label, value, units, selected, onClick, en}) =>
  <Box
    width={width}
    border={selected ? '1px solid white' : '0.5px solid gray'}
    borderRadius={2} px={1.5} py={0.6}
    display={'flex'}
    alignItems={'center'}
    justifyContent={'space-between'}
    onClick={onClick}
    sx={{
      cursor: 'pointer',
      backgroundColor: selected ? 'rgba(255,255,255,0.10)' : 'transparent',
      transition: 'background-color 0.2s ease, border-color 0.2s ease',
      '&:hover': {
        borderColor: selected ? 'white' : 'rgba(255,255,255,0.6)',
        backgroundColor: selected ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.05)',
      },
    }}
  >
    <Typography variant={'caption'} sx={{color: selected ? 'white' : 'grey', transition: 'color 0.2s ease'}}>
      {label}
    </Typography>

    <Box display={'flex'} alignItems={'baseline'} gap={0.5}>
      <Typography variant={'body2'} sx={{color: selected ? 'white' : 'grey', transition: 'color 0.2s ease'}}>
        {value?.toString().replace('.', en ? '.' : ',')}
      </Typography>
      <Typography variant={'body2'} sx={{color: selected ? 'white' : 'grey', transition: 'color 0.2s ease'}}>
        {units}
      </Typography>
    </Box>
  </Box>;
